import { useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { useCurrencies } from 'api/useCurrencies';
import usePricing from 'api/usePricing';
import usePublisher from 'api/usePublisher';
import { EIntervalsRepeat, EWeekDay } from 'constants/enums';
import {
  dayInMonthOptions,
  exchangeRateSchedulerIntervalsRepeatOptions
} from 'utils/selectOptionsUtils';
import { EToasterStatus, showToast } from 'utils/showToast';

import AcSelect from 'components/AcSelect/AcSelect';
import { AcSelectItem } from 'components/AcSelect/AcSelect.types';
import Button from 'components/Button/Button';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Modal } from 'components/Modal/Modal';
import RadioButtonGroup from 'components/RadioButton/RadioButtonGroup';

interface ExchangeRatesUpdaterManagerModalProps {
  isOpen: boolean;
  setIsExchangeRateManagerModalOpen: (open: boolean) => void;
}

const HARD_CODED_TIME_FRAMES = [
  EIntervalsRepeat.NONE,
  EIntervalsRepeat.QUARTERLY,
  EIntervalsRepeat.SEMI_ANNUALLY,
  EIntervalsRepeat.ANNUALLY
];

const ExchangeRatesUpdaterManagerModal: React.FC<
  ExchangeRatesUpdaterManagerModalProps
> = ({ isOpen, setIsExchangeRateManagerModalOpen }) => {
  const { getPublisherSettings } = usePublisher();
  const { getSupportedCurrencies, getDisabledCurrencies } = useCurrencies();
  const { updateExchangeRatesScheduler, updateExchangeRates } = usePricing();
  const enabledCurrencies: AcSelectItem[] = useMemo(
    () =>
      getSupportedCurrencies.data
        ?.filter(
          (currency: string) =>
            currency !== 'USD' &&
            !getDisabledCurrencies.data?.includes(currency)
        )
        .map((currency: string) => ({
          content: currency,
          key: currency,
          value: currency
        })),
    [getSupportedCurrencies.data, getDisabledCurrencies.data]
  );
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<EIntervalsRepeat>(
    EIntervalsRepeat.NONE
  );
  const [hour, setHour] = useState<number>(0);
  const [weeklyFrequencyFactor, setWeeklyFrequencyFactor] = useState<number>(0);
  const [monthlyFrequencyFactor, setMonthlyFrequencyFactor] =
    useState<number>(1);
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const isDisabled = useMemo(() => {
    if (isScheduled) return false;
    return selectedCurrencies.length === 0;
  }, [selectedCurrencies, isScheduled]);

  const submitUpdateExchangeRatesScheduler = async () => {
    try {
      await updateExchangeRatesScheduler.mutateAsync({
        frequency,
        hour,
        weeklyFrequencyFactor,
        monthlyFrequencyFactor,
        currenciesToRecalculate: selectedCurrencies
      });
      getPublisherSettings.refetch();
      showToast({
        message: 'Update schedule saved successfully.',
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      showToast({
        message: 'Failed to schedule exchange rates',
        status: EToasterStatus.ERROR
      });
    }
  };

  const submitUpdateExchangeRates = async () => {
    try {
      await updateExchangeRates.mutateAsync(selectedCurrencies);
      showToast({
        message: 'Exchange rates updated successfully',
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      showToast({
        message: 'Error updating exchange rates',
        status: EToasterStatus.ERROR
      });
    }
  };

  const submitUpdateExchangeRatesHandler = async () => {
    if (isScheduled) {
      await submitUpdateExchangeRatesScheduler();
    } else {
      await submitUpdateExchangeRates();
    }
    setIsExchangeRateManagerModalOpen(false);
  };

  useEffect(() => {
    if (enabledCurrencies?.length > 0) {
      setSelectedCurrencies(
        enabledCurrencies.map(
          (currency: AcSelectItem) => currency.value as string
        ) as string[]
      );
    }
  }, [enabledCurrencies]);

  useEffect(() => {
    if (isScheduled && getPublisherSettings.data) {
      setFrequency(
        getPublisherSettings.data.exchangeRatesUpdateScheduler?.frequency ||
          EIntervalsRepeat.NONE
      );
      setHour(
        getPublisherSettings.data.exchangeRatesUpdateScheduler?.hour || 0
      );
      setWeeklyFrequencyFactor(
        getPublisherSettings.data.exchangeRatesUpdateScheduler
          ?.weeklyFrequencyFactor || 0
      );
      setMonthlyFrequencyFactor(
        getPublisherSettings.data.exchangeRatesUpdateScheduler
          ?.monthlyFrequencyFactor || 1
      );
      if (getPublisherSettings.data.currenciesToRecalculate?.length > 0) {
        setSelectedCurrencies(
          getPublisherSettings.data.currenciesToRecalculate
        );
      }
    }
  }, [getPublisherSettings.data, enabledCurrencies, isScheduled]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsExchangeRateManagerModalOpen(false)}
      width="510px"
      className="!z-[999]"
    >
      <Box className="w-full flex flex-col gap-4">
        <Box className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">Update Exchange Rates</h2>
          <p className="text-sm text-gray-600">
            Keep exchange rates current with automatic daily, weekly, or monthly
            updates. Prices adjust using the latest available rates.
          </p>
        </Box>
        <AcSelect
          header="Currencies"
          sx={{ width: '100%', padding: '0px', height: '30px' }}
          size="small"
          value={selectedCurrencies}
          onChange={(e) => setSelectedCurrencies(e.target.value as string[])}
          items={enabledCurrencies}
          isCheckBox={true}
          multipleSelect={true}
          isSelectAllEnabled={true}
          isApplyBtnEnabled={true}
          setSelectedItems={setSelectedCurrencies}
          placeholder="No currencies selected"
          renderStatusTextOverride={() =>
            `${selectedCurrencies.length} ${selectedCurrencies.length === 1 ? 'currency' : 'currencies'} selected`
          }
        />
        <RadioButtonGroup
          value={!isScheduled ? 'Update Now' : 'Scheduled'}
          onValueChange={(value) => setIsScheduled(value === 'Scheduled')}
          className="border-r border-base-border h-6 pr-7"
          options={[
            {
              label: 'Update Now',
              value: 'Update Now'
            },
            {
              label: 'Scheduled',
              value: 'Scheduled'
            }
          ]}
        />

        {isScheduled && getPublisherSettings.data && (
          <ExchangeRateSchedulerContent
            schedulerRepeat={frequency}
            setSchedulerRepeat={setFrequency}
            weeklyFrequencyFactor={weeklyFrequencyFactor}
            setWeeklyFrequencyFactor={setWeeklyFrequencyFactor}
            monthlyFrequencyFactor={monthlyFrequencyFactor}
            setMonthlyFrequencyFactor={setMonthlyFrequencyFactor}
            hour={hour}
            setHour={setHour}
          />
        )}
        <Box className="flex gap-2 justify-end">
          <Button
            onClick={() => setIsExchangeRateManagerModalOpen(false)}
            variant="ghost"
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => submitUpdateExchangeRatesHandler()}
            variant="default"
            type="button"
            disabled={isDisabled}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExchangeRatesUpdaterManagerModal;

const ExchangeRateSchedulerContent: React.FC<{
  schedulerRepeat: EIntervalsRepeat;
  setSchedulerRepeat: (repeat: EIntervalsRepeat) => void;
  weeklyFrequencyFactor: number;
  setWeeklyFrequencyFactor: (factor: number) => void;
  monthlyFrequencyFactor: number;
  setMonthlyFrequencyFactor: (factor: number) => void;
  hour: number;
  setHour: (hour: number) => void;
}> = ({
  schedulerRepeat,
  setSchedulerRepeat,
  weeklyFrequencyFactor,
  setWeeklyFrequencyFactor,
  monthlyFrequencyFactor,
  setMonthlyFrequencyFactor,
  hour,
  setHour
}) => {
  return (
    <Box className="flex flex-col gap-2">
      <InputSelectDropdown
        shouldHighlightSelectedOption
        label="Repeat"
        contentClassName="z-[9999]"
        options={exchangeRateSchedulerIntervalsRepeatOptions.map((item) => ({
          value: String(item.value),
          label: item.content as string
        }))}
        defaultSelected={
          exchangeRateSchedulerIntervalsRepeatOptions
            .map((item) => ({
              value: String(item.value),
              label: item.content as string
            }))
            .find((opt) => opt.value === String(schedulerRepeat)) || {
            value: '',
            label: ''
          }
        }
        onOptionChange={(value) => {
          setSchedulerRepeat(value as EIntervalsRepeat);
        }}
      />
      {!HARD_CODED_TIME_FRAMES.includes(schedulerRepeat) && (
        <Box className="mt-2 flex flex-row gap-2">
          {schedulerRepeat !== EIntervalsRepeat.DAILY && (
            <Box style={{ width: '100%' }}>
              {schedulerRepeat === EIntervalsRepeat.WEEKLY && (
                <InputSelectDropdown
                  shouldHighlightSelectedOption
                  label="Weekday"
                  options={Object.keys(EWeekDay)
                    .filter((k) => isNaN(Number(k)))
                    .map((day, i) => ({
                      value: String(i),
                      label:
                        day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
                    }))}
                  defaultSelected={Object.keys(EWeekDay)
                    .filter((k) => isNaN(Number(k)))
                    .map((day, i) => ({
                      value: String(i),
                      label:
                        day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
                    }))
                    .find((opt) => opt.value === String(weeklyFrequencyFactor))}
                  onOptionChange={(value) => {
                    setWeeklyFrequencyFactor(Number(value));
                  }}
                />
              )}
              {schedulerRepeat === EIntervalsRepeat.MONTHLY && (
                <InputSelectDropdown
                  shouldHighlightSelectedOption
                  contentClassName="z-[9999]"
                  label="Day of month"
                  options={dayInMonthOptions.map((dayObject, i) => ({
                    value: String(i),
                    label: String(dayObject.content)
                  }))}
                  defaultSelected={dayInMonthOptions
                    .map((dayObject, i) => ({
                      value: String(i),
                      label: String(dayObject.content)
                    }))
                    .find(
                      (opt) => opt.value === String(monthlyFrequencyFactor - 1)
                    )}
                  onOptionChange={(value) => {
                    setMonthlyFrequencyFactor(Number(value));
                  }}
                />
              )}
            </Box>
          )}
          <Box
            style={{
              width: '100%'
            }}
          >
            <InputSelectDropdown
              shouldHighlightSelectedOption
              contentClassName="z-[9999]"
              label="Time (UTC)"
              options={Array.from({ length: 24 }, (_, i: number) => ({
                value: String(i),
                label: `${i < 10 ? '0' : ''}${i}:00`
              }))}
              defaultSelected={
                Array.from({ length: 24 }, (_, i: number) => ({
                  value: String(i),
                  label: `${i < 10 ? '0' : ''}${i}:00`
                })).find((opt) => opt.value === String(hour)) || {
                  value: '',
                  label: ''
                }
              }
              onOptionChange={(value) => setHour(Number(value))}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
